/* Cage Funnel — guided backflow enclosure/cage selector.
 * Steps: Model -> Insulated/Non (side-by-side) -> Size (with dimensions) -> Add-on
 * -> Add to quote (dispatches `quote:add` -> the quote drawer -> /admin estimate).
 *
 * Data-driven from the storefront enclosures-and-cages catalog. The cages are now
 * six GROUPED products (one product per model, with Size + Insulation as variant
 * options), so we match products by handle and expand each product's variants into
 * (insulation, size) buckets using the variants' selectedOptions. Pricing is
 * quote-only (no prices shown). Thumbnails use the product image where Shopify has
 * one so the buyer sees what they're picking at each step.
 */
if (!customElements.get('cage-funnel')) {
  // The six grouped enclosure products, matched by handle. Order here is the
  // order shown in the Model step. Products not in this list (accessories,
  // one-off custom cages) never appear as a model — the "Custom Cage" card
  // routes those to a quote request.
  const MODELS = [
    { key: 'liftoff', handle: 'lift-off-backflow-enclosure', label: 'Lift-Off', desc: 'Expanded-metal lift-off enclosure (BELO / BELOW)' },
    { key: 'aboveground', handle: 'above-ground-backflow-enclosure', label: 'Above-Ground', desc: 'Hinged-door enclosure with 100% access (E1A / E1AW)' },
    { key: 'clamshell', handle: 'clamshell-backflow-cage', label: 'Clamshell', desc: 'Two-door clamshell enclosure (E2A)' },
    { key: 'wallmount', handle: 'insulated-wall-mount-enclosure', label: 'Wall-Mount', desc: 'Insulated wall-mount enclosure (EOSW)' },
    { key: 'access2', handle: '2-door-access-steel-enclosure', label: '2-Door Access Steel', desc: 'Two-door full-access steel enclosure' },
    { key: 'access4', handle: '4-door-access-steel-enclosure', label: '4-Door Access Steel', desc: 'Four-door full-access steel enclosure' },
  ];
  const MODEL_BY_HANDLE = Object.fromEntries(MODELS.map((m) => [m.handle, m]));

  // Curated model beauty shots, served from the app's public folder
  // (app.thebackflowdepot.com/cages-enclosures/…). These take precedence over
  // the Shopify featured image so every card shows a consistent product photo.
  // Insulation-specific: `non` = open expanded-metal screen, `ins` = solid
  // galvanized panel behind the mesh — so the Insulation step, Size cards and
  // summary all show what that build actually looks like. Add more here as
  // photos are uploaded for the remaining models.
  const MODEL_IMAGES = {
    liftoff: { non: '/cages-enclosures/liftoff.jpg', ins: '/cages-enclosures/liftoff-insulated.jpg' },
    aboveground: { non: '/cages-enclosures/aboveground.jpg', ins: '/cages-enclosures/aboveground-insulated.jpg' },
  };

  // Inside-dimension lookup (from PW spec sheets) — W x L x H inches. Keyed by
  // family|size. Only sizes we have confirmed dims for are listed; anything
  // else shows the size label alone (dimensions are confirmed on the quote).
  const DIMS = {
    // Lift-Off is now Size {XS,S,M,L} × Insulation; both builds share the frame
    // footprint per size (formerly the BELO 25/35/41/49 sizes).
    'liftoff|XS': '14"W × 24"L × 27"H', 'liftoff|S': '14"W × 34"L × 27"H',
    'liftoff|M': '14"W × 40"L × 27"H', 'liftoff|L': '14"W × 49"L × 27"H',
    'aboveground|XS': '12"W × 24"L × 26"H', 'aboveground|S': '14"W × 30"L × 24"H',
    'aboveground|M': '17"W × 40"L × 30"H', 'aboveground|L': '20"W × 47"L × 36"H',
  };
  const SIZE_LABELS = { XS: 'Extra Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'Extra Large' };
  // Numeric lift-off sizes sort after the letter sizes; letters follow this order.
  const SIZE_ORDER = { XS: 0, S: 1, M: 2, L: 3, XL: 4 };

  // Per-model, per-insulation spec sheets (PDF), served from the app's public
  // folder (app.thebackflowdepot.com/cages-enclosures/…). Keyed family|non|ins.
  // One sheet covers all sizes of that model/build; shown on the Size step. Add
  // more here as PDFs are uploaded for the remaining models.
  const SPEC_SHEETS = {
    'liftoff|non': '/cages-enclosures/PW-BELO-STD.pdf',
    'liftoff|ins': '/cages-enclosures/PW-BELOW-STD.pdf',
    'aboveground|ins': '/cages-enclosures/PW-E1AW-STD.pdf',
  };

  const PLACEHOLDER = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><rect x="3" y="6" width="18" height="14" rx="1.5"/><path d="M3 10h18M9 6V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V6"/></svg>';

  class CageFunnel extends HTMLElement {
    constructor() {
      super();
      this.models = []; // [{ ...MODEL, image, tree: { ins:[], non:[] }, insAvail }]
      this.embedFrames = {}; // size -> { variantId, title } — Lift-Off concrete embed frames
      this.state = { family: null, insulated: null, size: null, addon: false, addonFrame: false };
    }

    connectedCallback() {
      this.apiBase = (this.dataset.apiBase || 'https://app.thebackflowdepot.com').replace(/\/$/, '');
      this.addonVariant = this.dataset.addonVariant || '';
      this.addonTitle = this.dataset.addonTitle || 'Red-head anchor bolts (4-pack)';
      this.innerHTML = '<p class="cage-funnel__loading">Loading enclosure options…</p>';
      this.load();
    }

    async load() {
      try {
        const res = await fetch(`${this.apiBase}/api/storefront/parts-finder/products?scope=enclosures-and-cages&category=enclosures-and-cages&limit=100`);
        const data = await res.json();
        this.build(data.products || []);
        this.render();
      } catch (e) {
        this.innerHTML = '<p class="cage-funnel__loading">Couldn’t load enclosures. Please call (916) 251-7071 and we’ll help you find the right cage.</p>';
      }
    }

    // Build one entry per known grouped product, expanding its variants into
    // insulated / non-insulated buckets keyed by size.
    build(products) {
      const byKey = {};
      for (const p of products) {
        const model = MODEL_BY_HANDLE[p.handle];
        if (!model) continue; // accessory / custom one-off — not a funnel model
        const curated = MODEL_IMAGES[model.key] || {};
        const abs = (path) => (path ? this.apiBase + path : null);
        // Per-insulation image, falling back to the other insulation's photo,
        // then the Shopify featured image, then a placeholder.
        const insImage = abs(curated.ins) || abs(curated.non) || p.imageUrl || null;
        const nonImage = abs(curated.non) || abs(curated.ins) || p.imageUrl || null;
        const entry = byKey[model.key] || {
          ...model, images: { ins: insImage, non: nonImage }, tree: { ins: [], non: [] },
        };
        for (const v of p.variants || []) {
          if (!v.variantId) continue;
          const size = this.sizeOf(v);
          if (!size) continue;
          const insulated = this.isInsulated(v);
          entry.tree[insulated ? 'ins' : 'non'].push({
            size,
            variantId: v.variantId,
            dims: DIMS[`${model.key}|${size}`] || null,
            image: entry.images[insulated ? 'ins' : 'non'],
            title: p.title,
          });
        }
        byKey[model.key] = entry;
      }
      // Keep only models that resolved to at least one variant, in MODELS order,
      // and pre-sort each bucket's sizes.
      this.models = MODELS
        .map((m) => byKey[m.key])
        .filter((e) => e && (e.tree.ins.length || e.tree.non.length))
        .map((e) => {
          e.tree.ins = this.dedupeSizes(e.tree.ins);
          e.tree.non = this.dedupeSizes(e.tree.non);
          e.insAvail = { ins: e.tree.ins.length > 0, non: e.tree.non.length > 0 };
          return e;
        });

      // Concrete embed frames — one per size, poured-slab anchor. They fit the
      // Lift-Off (BELO/BELOW) sizes identically, so we key them by size and offer
      // them as an optional add-on on the summary step. Matched by handle so a
      // variant-id change never breaks the mapping.
      this.embedFrames = {};
      for (const p of products) {
        if (!/concrete-embed-frame/i.test(p.handle || '')) continue;
        const v = (p.variants || [])[0];
        const size = (p.sizes || [])[0];
        if (v && v.variantId && size) this.embedFrames[size] = { variantId: v.variantId, title: p.title };
      }
    }

    // Prefer the Size option; fall back to the first variant-title segment
    // (single-option products like Wall-Mount / Lift-Off title = the size).
    sizeOf(v) {
      const opt = this.optionValue(v, ['size']);
      if (opt) return opt.trim();
      const first = (v.title || '').split('/')[0].trim();
      return first && first.toLowerCase() !== 'default title' ? first : null;
    }

    // Insulation option wins; else read it off the SKU model code — the trailing
    // "W" (BELOW, E1AW, E2AW, EOSW, FTW, BE4DW) marks the insulated build.
    isInsulated(v) {
      const opt = this.optionValue(v, ['insulation', 'insulated']);
      if (opt) return !/non/i.test(opt);
      const t = (v.title || '').toUpperCase();
      if (/NON[\s-]?INSULAT/.test(t)) return false;
      if (/INSULAT/.test(t)) return true;
      const code = (v.sku || '').replace(/^PW\//i, '').split(/[-\s(]/)[0];
      return /W$/i.test(code);
    }

    optionValue(v, names) {
      for (const o of v.selectedOptions || []) {
        if (names.includes((o.name || '').toLowerCase())) return o.value;
      }
      return null;
    }

    dedupeSizes(list) {
      const seen = new Map();
      for (const p of list) if (!seen.has(p.size)) seen.set(p.size, p);
      return [...seen.values()].sort((a, b) => {
        const ao = SIZE_ORDER[a.size], bo = SIZE_ORDER[b.size];
        if (ao != null && bo != null) return ao - bo;
        if (ao != null) return -1;
        if (bo != null) return 1;
        return String(a.size).localeCompare(String(b.size), undefined, { numeric: true });
      });
    }

    modelFor(key) { return this.models.find((m) => m.key === key) || null; }

    // Spec-sheet PDF URL for the chosen model + insulation, or null if none yet.
    specSheetFor(family, insulated) {
      const path = SPEC_SHEETS[`${family}|${insulated ? 'ins' : 'non'}`];
      return path ? this.apiBase + path : null;
    }

    sizesFor(key, insulated) {
      const m = this.modelFor(key);
      return (m && m.tree[insulated ? 'ins' : 'non']) || [];
    }

    selectedProduct() {
      return this.sizesFor(this.state.family, this.state.insulated).find((p) => p.size === this.state.size) || null;
    }

    setState(patch) { Object.assign(this.state, patch); this.render(); }

    // Selecting a model: if only one insulation exists (e.g. Wall-Mount is
    // insulated-only), auto-pick it and jump straight to Size.
    selectFamily(key) {
      const m = this.modelFor(key);
      let insulated = null;
      if (m && m.insAvail.ins && !m.insAvail.non) insulated = true;
      else if (m && m.insAvail.non && !m.insAvail.ins) insulated = false;
      this.setState({ family: key, insulated, size: null });
    }

    addToQuote() {
      const prod = this.selectedProduct();
      if (!prod) return;
      document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: prod.variantId, title: prod.title, handle: this.modelFor(this.state.family)?.handle, quantity: 1 } }));
      if (this.state.addon && this.addonVariant) {
        document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: this.addonVariant, title: this.addonTitle, quantity: 1 } }));
      }
      // Concrete embed frame add-on (Lift-Off only, size-matched).
      const frame = this.state.family === 'liftoff' ? this.embedFrames[this.state.size] : null;
      if (this.state.addonFrame && frame) {
        document.dispatchEvent(new CustomEvent('quote:add', { detail: { variantId: frame.variantId, title: frame.title, quantity: 1 } }));
      }
    }

    thumb(image, extraClass) {
      const inner = image ? `<img src="${this.esc(image)}" alt="" loading="lazy">` : PLACEHOLDER;
      return `<span class="cage-funnel__thumb${extraClass ? ' ' + extraClass : ''}">${inner}</span>`;
    }

    render() {
      const s = this.state;
      const model = s.family && s.family !== 'custom' ? this.modelFor(s.family) : null;
      const isCustom = s.family === 'custom';
      let step = 1;
      if (s.family) step = 2;
      if (s.family && s.insulated !== null) step = 3;
      if (s.family && s.insulated !== null && s.size) step = 4;

      const crumb = (n, label, active, done) =>
        `<button type="button" class="cage-funnel__pill${active ? ' is-active' : ''}${done ? ' is-done' : ''}" data-goto="${n}"${done ? '' : ' disabled'}>${n}. ${label}</button>`;

      let body = '';
      if (step === 1) {
        const cards = this.models.map((m) =>
          `<button type="button" class="cage-funnel__card" data-family="${m.key}">${this.thumb(m.images && (m.images.non || m.images.ins))}<span class="cage-funnel__card-title">${m.label}</span><span class="cage-funnel__card-desc">${m.desc}</span></button>`
        );
        cards.push(`<button type="button" class="cage-funnel__card" data-family="custom">${this.thumb(null)}<span class="cage-funnel__card-title">Custom Cage</span><span class="cage-funnel__card-desc">Need a non-standard size or configuration? We build to spec.</span></button>`);
        body = `<div class="cage-funnel__grid">${cards.join('')}</div>`;
      } else if (isCustom) {
        body = `<div class="cage-funnel__custom">
          <p>Tell us about the assembly you need to protect — sizes, insulation, location — and we'll build a custom enclosure and send you an estimate.</p>
          <a class="btn btn--primary" href="/pages/contact">Request a custom cage quote</a>
          <p class="cage-funnel__or">or call <strong>(916) 251-7071</strong></p>
        </div>`;
      } else if (step === 2) {
        const avail = model ? model.insAvail : { ins: false, non: false };
        const card = (ins, title, desc) => avail[ins ? 'ins' : 'non']
          ? `<button type="button" class="cage-funnel__compare" data-insulated="${ins ? '1' : '0'}">${this.thumb(model && model.images && model.images[ins ? 'ins' : 'non'])}<span class="cage-funnel__compare-title">${title}</span><span class="cage-funnel__compare-desc">${desc}</span><span class="cage-funnel__compare-price">Pricing on quote</span></button>`
          : '';
        body = `<div class="cage-funnel__compare-row">
          ${card(false, 'Non-Insulated', 'Open expanded-metal screen — ventilation &amp; security.')}
          ${card(true, 'Insulated', 'Galvanized panel + 1" bonded foam — freeze protection.')}
        </div>`;
      } else if (step === 3) {
        const sizes = this.sizesFor(s.family, s.insulated);
        const spec = this.specSheetFor(s.family, s.insulated);
        const specLink = spec
          ? `<a class="cage-funnel__spec" href="${this.esc(spec)}" target="_blank" rel="noopener"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6M9 13h6M9 17h6"/></svg>View the ${model ? this.esc(model.label) : ''} spec sheet (PDF)</a>`
          : '';
        body = specLink + `<div class="cage-funnel__grid">` + sizes.map((p) => {
          const sl = SIZE_LABELS[p.size] || (/^\d+$/.test(p.size) ? p.size + '"' : p.size);
          return `<button type="button" class="cage-funnel__card" data-size="${this.esc(p.size)}">${this.thumb(p.image)}<span class="cage-funnel__card-title">${sl}</span><span class="cage-funnel__card-desc">${p.dims || 'Dimensions confirmed on quote'}</span></button>`;
        }).join('') + `</div>`;
      } else if (step === 4) {
        const prod = this.selectedProduct();
        // Concrete embed frame — Lift-Off only, matched to the chosen size.
        const frame = s.family === 'liftoff' ? this.embedFrames[s.size] : null;
        const frameAddon = frame ? `
          <label class="cage-funnel__addon">
            <input type="checkbox" data-addon-frame ${s.addonFrame ? 'checked' : ''}>
            <span><strong>Add a concrete embed frame</strong><br>Anchors the enclosure into a poured concrete slab. Sized to match — fits both insulated &amp; non-insulated.</span>
          </label>` : '';
        body = `<div class="cage-funnel__summary">
          ${this.thumb(prod && prod.image, 'cage-funnel__summary-thumb')}
          <p class="cage-funnel__summary-line"><strong>${prod ? this.esc(prod.title) : ''}</strong></p>
          <p class="cage-funnel__summary-dims">${model ? model.label : ''} · ${SIZE_LABELS[s.size] || s.size}${s.insulated ? ' · Insulated' : ' · Non-Insulated'}${prod && prod.dims ? ' · ' + prod.dims : ''}</p>
          <label class="cage-funnel__addon">
            <input type="checkbox" data-addon ${s.addon ? 'checked' : ''}>
            <span><strong>Add red-head anchor bolts (4-pack)</strong><br>Recommended for concrete-pad mounting.</span>
          </label>
          ${frameAddon}
          <button type="button" class="btn btn--primary cage-funnel__submit" data-add-to-quote>Add to quote →</button>
          <p class="cage-funnel__note">Pricing is provided on your quote — a real backflow specialist confirms availability, lead time &amp; price, then emails you an estimate to approve.</p>
        </div>`;
      }

      this.innerHTML = `
        <div class="cage-funnel__steps">
          ${crumb(1, 'Model', step === 1, true)}
          ${crumb(2, 'Insulation', step === 2, !!s.family && !isCustom)}
          ${crumb(3, 'Size', step === 3, s.insulated !== null && !isCustom)}
          ${crumb(4, 'Add-ons', step === 4, !!s.size && !isCustom)}
        </div>
        <div class="cage-funnel__panel">${body}</div>`;
      this.bind();
    }

    bind() {
      this.querySelectorAll('[data-family]').forEach((el) => el.addEventListener('click', () => this.selectFamily(el.dataset.family)));
      this.querySelectorAll('[data-insulated]').forEach((el) => el.addEventListener('click', () => this.setState({ insulated: el.dataset.insulated === '1', size: null })));
      this.querySelectorAll('[data-size]').forEach((el) => el.addEventListener('click', () => this.setState({ size: el.dataset.size })));
      this.querySelector('[data-addon]')?.addEventListener('change', (e) => { this.state.addon = e.target.checked; });
      this.querySelector('[data-addon-frame]')?.addEventListener('change', (e) => { this.state.addonFrame = e.target.checked; });
      this.querySelector('[data-add-to-quote]')?.addEventListener('click', () => this.addToQuote());
      this.querySelectorAll('[data-goto]:not([disabled])').forEach((el) => el.addEventListener('click', () => {
        const n = Number(el.dataset.goto);
        if (n === 1) this.setState({ family: null, insulated: null, size: null });
        else if (n === 2) this.setState({ insulated: null, size: null });
        else if (n === 3) this.setState({ size: null });
      }));
    }

    esc(s) { return String(s).replace(/"/g, '&quot;'); }
  }
  customElements.define('cage-funnel', CageFunnel);
}
